import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CotizacionPausaEstado } from '../../Models/cotizacion-pausa-estado.enum';
import { CreateLead, RetakeDuplicatedResponse } from '../../Models/createLead';
import { LeadResponse } from '../../Models/leadResponse';
import { NoHaySeguroEstado } from '../../Models/no-hay-seguro-estado.enum';
import { saveToStorageAsync } from '../../helpers/storageUtils';
import {
  CONSULTA_RUNT,
  COTIZACION_PAUSA,
  FEEDBACK,
  NO_PREPARADOS,
  SIN_ZONA,
  YATEMENOSDATOS
} from '../../routes.constans';
import { LeadsService } from '../../services/Leads/leads.service';
import { ExceptionService } from '../../services/excepciones/exception.service';
import { PROBLEMAS_TECNICOS_REDIRECT } from '../../shared/final-pages/final-pages.constants';
import { setData, updateResponse } from '../../storage/Lead/data.actions';
import { LeadApiMessage } from './lead-api-messages.enum';

const VALOR_ASEGURABLE_MAX_DIGITAL = 236372175;

// Interfaz para describir una acción de respuesta
interface ResponseAction {
  route: string;
  dispatch: boolean; // ¿Debe hacer dispatch al store?
  page?: string;
  state?: number;
}

// Diccionario que mapea un mensaje a una acción
const MESSAGE_TO_ACTION_MAP: Record<LeadApiMessage, ResponseAction> = {
  // --- Acciones que navegan a COTIZACION_PAUSA ---
  [LeadApiMessage.TipoDocumentoInvalido]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_PROPIETARIO_PERSONA_JURIDICA,
  },
  [LeadApiMessage.TipoUsoInvalido]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_USO_COMERCIAL, // validar
  },
  [LeadApiMessage.TipoCombustibleInvalido]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.VALIDACION_COMBUSTIBLE_FALLIDA, // validar moto electrica
  },
  [LeadApiMessage.ValorMaximoAsegurado]: {
    route: YATEMENOSDATOS,
    dispatch: true,
    // page: 'cotizacion_pausa_estado',
    // state: CotizacionPausaEstado.FALLA_TECNICA_COTIZADOR, // validar
  },
  [LeadApiMessage.ValidacionCilindraje]: {
    route: YATEMENOSDATOS,
    dispatch: true,
    // page: 'cotizacion_pausa_estado',
    // state: CotizacionPausaEstado.VALIDACION_MOVILIDAD_FALLIDA, // validar
  },
  [LeadApiMessage.ErrorServicioFasecolda]: {
    route: PROBLEMAS_TECNICOS_REDIRECT,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_FALLA_SERVICIO_FASECOLDA, //
  },
  [LeadApiMessage.ErrorDatosVaciosFasecolda]: {
    route: PROBLEMAS_TECNICOS_REDIRECT,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_FALLA_SERVICIO_FASECOLDA, //
  },

  [LeadApiMessage.LeadErrado]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_FALLA_RUNT,
  },

  [LeadApiMessage.ValidacionAntiguedad]: {
    route: YATEMENOSDATOS,
    dispatch: true,
  },

  [LeadApiMessage.SalvamentoSuraUnaVez]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.VALIDACION_SALVAMENTO,
  },

  // --- Acciones que navegan a NO_PREPARADOS ---
  [LeadApiMessage.ZonaCirculacionInvalida]: {
    route: SIN_ZONA,
    dispatch: true,
    page: 'no_hay_seguro_estado',
    state: NoHaySeguroEstado.ZONA_CIRCULACION,
  },
  [LeadApiMessage.PoliticaLineaNoPermitida]: {
    route: NO_PREPARADOS,
    dispatch: true,
    page: 'no_hay_seguro_estado',
    state: NoHaySeguroEstado.MARCA_LINEA_NO_PERMITIDA,
  },

  [LeadApiMessage.SalvamentoNoSura]: {
    route: NO_PREPARADOS,
    dispatch: true,
    page: 'no_hay_seguro_estado',
    state: NoHaySeguroEstado.MARCA_LINEA_NO_PERMITIDA,
  },

  [LeadApiMessage.SalvamentoSuraNoAsegurable]: {
    route: NO_PREPARADOS,
    dispatch: true,
    page: 'no_hay_seguro_estado',
    state: NoHaySeguroEstado.MARCA_LINEA_NO_PERMITIDA,
  },

  [LeadApiMessage.ErrorServicioRunt]: {
    route: PROBLEMAS_TECNICOS_REDIRECT,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_FALLA_RUNT,
  },

  [LeadApiMessage.ErrorDatosVaciosRunt]: {
    route: COTIZACION_PAUSA,
    dispatch: true,
    page: 'cotizacion_pausa_estado',
    state: CotizacionPausaEstado.LEAD_FALLA_RUNT,
  },

  [LeadApiMessage.EstadoVehiculoInvalido]: {
    route: YATEMENOSDATOS,
    dispatch: true,
    // page: 'no_hay_seguro_estado',
    // state: CotizacionPausaEstado.LEAD_PRENDA_SI, // verificar
  },
  [LeadApiMessage.CilindrajeInvalido]: {
    route: YATEMENOSDATOS,
    dispatch: true,
    page: 'ya_tenemos_datos',
    // state: CotizacionPausaEstado.CILINDRAJE_INVALIDO,
  },
};

// ... (imports, constantes y diccionario de antes)

/**
 * Navega a la pantalla de consulta RUNT, añadiendo el código de asesor si existe.
 */
function navigateToConsultaRunt(router: Router, codAsesor?: string): void {
  const queryParams = codAsesor ? { codigoAsesor: codAsesor } : {};
  router.navigate([CONSULTA_RUNT], { queryParams });
}

function handleRetakeDuplicateLead(
  response: RetakeDuplicatedResponse,
  store: Store,
  router: Router,
  codAsesor?: string,
): void {
  // @TODO
  // Se puede refactorizar dentro de la función handleCreateLeadError si es necesario
  // pero se debe validar el mensaje de error en la respuesta 'Lead duplicado' o 'Lead Duplicado'?;

  // if (response.message === 'Lead duplicado' && response.data.urlRetoma !== '') {
  //   saveToStorageAsync('UrlRetoma', response.data.urlRetoma, 'session');
  //   store.dispatch(setData({ key: 'UrlRetoma', value: response.data.urlRetoma, merge: true }));
  //   router.navigate([RETOMA_DUPLICADO]);
  //   return;
  // }

  // if (response.message === 'Lead duplicado' && response.data.urlRetoma === '') {
  //   console.log('URL de retoma no proporcionada para lead duplicado.');
  //   router.navigate([YATEMENOSDATOS]);
  //   return;
  // }

  if (!response.error && !!response.data?.Id) {
    navigateToConsultaRunt(router, codAsesor);
  } else {
    saveToStorageAsync('cotizacion_pausa_estado', 100, 'session'); // falla tecnica
    response.message === LeadApiMessage.LeadErrado
      ? router.navigate([FEEDBACK])
      : router.navigate([COTIZACION_PAUSA]);
  }
}



export async function handleLeadCreationResponse(
  response: LeadResponse,
  router: Router,
  store: Store,
  codAsesor?: string,
  leadsService?: LeadsService,
  exceptionService?: ExceptionService,
  sessionId?: string,
  leadRequestData?: CreateLead,
): Promise<void> {
  const insurableValue =
    //algunos backends lo exponen con nombres diferentes
    (response as any)?.data?.vehicle?.insurable_value ??
    (response as any)?.data?.vehicle?.insurable_value_runt ??
    (response as any)?.data?.vehicle?.insurableValue ??
    0;

  const insurableValueNumber = Number(insurableValue) || 0;


  // Rescate asistido por Antigüedad > 10 años:
  // - Corta flujo digital
  // - Envía excepción con información mínima de prospecto/vehículo
  // - Navega a "Ya tenemos tus datos"
  if (response.message === LeadApiMessage.ValidacionAntiguedad && leadsService && sessionId) {
    const nowYear = new Date().getFullYear();
    const vehicleModelYear = Number(response?.data?.vehicle?.model) || undefined;
    const antiguedadCalculada =
      vehicleModelYear && !Number.isNaN(vehicleModelYear) ? nowYear - vehicleModelYear : undefined;

    // Solo se setea para este caso (según tu instrucción)
    store.dispatch(setData({ key: 'estadoDiscador', value: 'Pendiente', merge: false }));

    const leadAntiguedad: any = {
      ...(leadRequestData || {}),
      required_status: 'Antigüedad',
      estadoDiscador: 'Pendiente',
      estadoRetoma: 'Habilitado',
      estadoRescate: 'Habilitado',
      Leadstatus: 'abierto',
      percentege_lead: 20,
      motivo_rescate: 'Antigüedad superior a 10 años',
      vehicle_model_year: vehicleModelYear,
      antiguedad_calculada: antiguedadCalculada,
      pantalla: 'ValidacionAntiguedad',
    };

    // 1) Crear lead (SFI)
    leadsService.postCrearLeads(leadAntiguedad, sessionId).subscribe({
      next: (resp) => {
        console.log('Lead rescate antigüedad creado:', resp);
        if (resp) {
          router.navigate([YATEMENOSDATOS]);
          return;
        }
      },
      error: (err) => console.error('Error creando lead rescate antigüedad:', err),
    });

    // 2) ExceptionLead (Mulesoft)
    // exceptionService
    //   .sendGeneralException(sessionId, response?.data?.Id || '', leadAntiguedad as bodyException)
    //   .subscribe({
    //     next: (resp) => console.log('ExceptionLead antigüedad enviada:', resp),
    //     error: (err) => console.error('Error enviando ExceptionLead antigüedad:', err),
    //   });

    return;
  }


  // CA5: Valor asegurable >= 236.372.176 => corta flujo + crea lead de rescate + reporta exceptionLead
  if (
    insurableValueNumber >= VALOR_ASEGURABLE_MAX_DIGITAL + 1 &&
    leadsService &&
    exceptionService &&
    sessionId
  ) {
    store.dispatch(setData({ key: 'estadoDiscador', value: 'Pendiente', merge: false }));

    const leadRescate: any = {
      ...(leadRequestData || {}),
      required_status: 'Supera valor asegurado permitido',
      estadoDiscador: 'Pendiente',
      estadoRetoma: 'Habilitado',
      estadoRescate: 'Habilitado',
      Leadstatus: 'abierto',
      percentege_lead: 20,
      // trazabilidad
      pantalla: 'ValorAsegurableMaxDigital',
    };

    // 1) Crear lead (SFI)
    leadsService.postCrearLeads(leadRescate, sessionId).subscribe({
      next: (resp) => {
        console.log('Lead rescate Supera Valor Asegurado Permitido creado:', resp);
        if (resp) {
          router.navigate([YATEMENOSDATOS]);
          return;
        }
      },
      error: (err) => console.error('Error creando lead rescate valor asegurable:', err),
    });

    // ExceptionLead (Mulesoft) con los mismos datos del caso
    // exceptionService
    //   .sendGeneralException(sessionId, response?.data?.Id || '', leadRescate as bodyException)
    //   .subscribe({
    //     next: (resp) => console.log('ExceptionLead valor asegurable enviada:', resp),
    //     error: (err) => console.error('Error enviando ExceptionLead valor asegurable:', err),
    //   });

    return;
  }

  // CA6: Valor asegurable <= 236.372.175 => crea lead principal + reporta exceptionLead y continúa flujo normal
  if (insurableValueNumber <= VALOR_ASEGURABLE_MAX_DIGITAL && leadsService && exceptionService && sessionId) {
    store.dispatch(setData({ key: 'estadoDiscador', value: 'Pendiente', merge: false }));

    // const leadPrincipal: any = {
    //   ...(leadRequestData || {}),
    //   required_status: 'lead principal',
    //   estadoDiscador: 'Pendiente',
    //   estadoRetoma: 'Habilitado',
    //   estadoRescate: 'Habilitado',
    //   Leadstatus: 'abierto',
    //   percentege_lead: 70,
    //   pantalla: 'LeadPrincipal',
    // };

    // // 1) Crear lead (SFI)
    // leadsService.postCrearLeads(leadPrincipal, sessionId).subscribe({
    //   next: (resp) => {
    //     console.log('Lead rescate Valor Asegurado Permitido creado:', resp);
    //   },
    //   error: (err) => console.error('Error creando lead principal:', err),
    // });

    // 2) ExceptionLead (Mulesoft)
    // exceptionService
    //   .sendGeneralException(sessionId, response?.data?.Id || '', leadPrincipal as bodyException)
    //   .subscribe({
    //     next: (resp) => console.log('ExceptionLead lead principal enviada:', resp),
    //     error: (err) => console.error('Error enviando ExceptionLead lead principal:', err),
    //   });
  }





  const action = MESSAGE_TO_ACTION_MAP[response.message as LeadApiMessage];
  if (action) {
    if (action.dispatch) {
      store.dispatch(updateResponse({ leadR: response }));
    }

    // Manejo de casos especiales de navegación
    if (action.route === CONSULTA_RUNT) {
      navigateToConsultaRunt(router, codAsesor);
    } else {
      if (action.page !== undefined) {
        console.log('action.page xxx', action.page);
        await saveToStorageAsync(action.page, action.state, 'session');
      }
      console.log(
        `Redirigiendo a: ${action.route} con estado: ${action.state} y página: ${action.page}`,
      );

      router.navigate([action.route]);
    }
  } else {
    handleRetakeDuplicateLead(
      response as unknown as RetakeDuplicatedResponse,
      store,
      router,
      codAsesor,
    );
    // Acción por defecto si el mensaje no está en el mapa
    console.warn(`Mensaje no manejado: "${response.message}". Se aplica acción por defecto.`);
    store.dispatch(updateResponse({ leadR: response }));
  }
}

/**
 * Manejador de errores HTTP. Transforma el error en un objeto consistente.
 */
export function handleHttpError(error: any): {
  message: string;
  error: string;
} {
  // Si el error ya fue interceptado (status 500+), no debería llegar aquí
  // pero agregamos esta verificación por seguridad
  if (error.status >= 500) {
    console.warn(
      'Error 500+ llegó al handleHttpError después del interceptor - esto no debería ocurrir',
    );
  }

  switch (error.status) {
    case 422:
      try {
        const dataError = JSON.parse(error.error.message);

        return { error: '422', message: dataError.data.message };
      } catch (e) {
        return { error: '422', message: error.error?.message || 'Error al parsear respuesta.' };
      }
    case 406:
      return { error: '406', message: LeadApiMessage.LeadErrado };
    case 500:
    default:
      return {
        error: String(error.status),
        message: LeadApiMessage.LeadErrado,
      };
  }
}
